import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { useLocation } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { ExternalLink, Mic, RotateCcw, Send, Square, Trash2 } from "lucide-react";

import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { toSpeechWav } from "@/lib/wav";

// Voice notes to the Veez agent, for anyone who would rather not post a voice
// message in a Discord channel. Record here; Veez posts it in Discord, opens a
// thread on it, answers there and tags you — carry on in that thread.
// Backend: convex/voiceNotes.ts.

/** 16 kHz WAV is 32 KB/s, so 4 minutes is the most that fits Discord's 8 MB. */
const MAX_SECONDS = 240;

type Phase =
  | { kind: "idle" }
  | { kind: "recording"; startedAt: number }
  | { kind: "processing" }
  | { kind: "review"; wav: Blob; url: string; durationSecs: number }
  | { kind: "sending" };

function clock(secs: number) {
  const s = Math.max(0, Math.floor(secs));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function VoiceNoteButton() {
  const pathname = useLocation({ select: (s) => s.pathname });
  const [open, setOpen] = React.useState(false);
  const [phase, setPhase] = React.useState<Phase>({ kind: "idle" });
  const [error, setError] = React.useState<string | null>(null);
  const [now, setNow] = React.useState(() => Date.now());

  const recorder = React.useRef<MediaRecorder | null>(null);
  const discardOnStop = React.useRef(false);

  const generateUploadUrl = useMutation(api.voiceNotes.generateUploadUrl);
  const send = useMutation(api.voiceNotes.send);

  const recording = phase.kind === "recording";
  const elapsed = recording ? (now - phase.startedAt) / 1000 : 0;

  // Tick the timer, and stop at the cap rather than produce a file Discord refuses.
  React.useEffect(() => {
    if (!recording) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [recording]);
  React.useEffect(() => {
    if (recording && elapsed >= MAX_SECONDS) recorder.current?.stop();
  }, [recording, elapsed]);

  // Free the preview URL when it is replaced or the component goes away.
  const reviewUrl = phase.kind === "review" ? phase.url : null;
  React.useEffect(() => {
    if (!reviewUrl) return;
    return () => URL.revokeObjectURL(reviewUrl);
  }, [reviewUrl]);

  // Release the microphone if the app unmounts mid-recording.
  React.useEffect(
    () => () => {
      discardOnStop.current = true;
      if (recorder.current?.state === "recording") recorder.current.stop();
    },
    [],
  );

  async function start() {
    setError(null);
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch {
      setError("Microphone access was blocked. Allow it for this site and try again.");
      return;
    }
    const chunks: Blob[] = [];
    const rec = new MediaRecorder(stream);
    discardOnStop.current = false;
    rec.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    rec.onstop = async () => {
      stream.getTracks().forEach((t) => t.stop());
      recorder.current = null;
      if (discardOnStop.current) {
        setPhase({ kind: "idle" });
        return;
      }
      setPhase({ kind: "processing" });
      try {
        const { wav, durationSecs } = await toSpeechWav(
          new Blob(chunks, { type: rec.mimeType }),
        );
        if (durationSecs < 1) {
          setError("That was too short to send.");
          setPhase({ kind: "idle" });
          return;
        }
        setPhase({ kind: "review", wav, url: URL.createObjectURL(wav), durationSecs });
      } catch {
        setError("Could not process the recording. Please try again.");
        setPhase({ kind: "idle" });
      }
    };
    recorder.current = rec;
    rec.start(1000);
    const startedAt = Date.now();
    setNow(startedAt);
    setPhase({ kind: "recording", startedAt });
    setOpen(true);
  }

  function stop(discard: boolean) {
    discardOnStop.current = discard;
    recorder.current?.stop();
  }

  async function submit() {
    if (phase.kind !== "review") return;
    const { wav, durationSecs } = phase;
    setPhase({ kind: "sending" });
    setError(null);
    try {
      const uploadUrl = await generateUploadUrl();
      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": "audio/wav" },
        body: wav,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const { storageId } = (await res.json()) as { storageId: Id<"_storage"> };
      await send({
        storageId,
        durationSecs,
        pagePath: pathname,
        pageTitle: document.title || undefined,
      });
      setPhase({ kind: "idle" });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send the voice note.");
      // Keep the recording so it can be sent again without re-recording.
      setPhase({ kind: "review", wav, url: URL.createObjectURL(wav), durationSecs });
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label={recording ? "Recording voice note" : "Voice note to Veez"}
            className="relative"
          />
        }
      >
        <Mic className={recording ? "text-destructive" : undefined} />
        {recording && (
          <span className="absolute top-1.5 right-1.5 size-2 animate-pulse rounded-full bg-destructive" />
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <PopoverHeader>
          <PopoverTitle>Voice note to Veez</PopoverTitle>
          <PopoverDescription>
            Only a transcript is posted to Discord — never your voice. Veez
            answers in a thread there and tags you. It also knows which HQ page
            you are on.
          </PopoverDescription>
        </PopoverHeader>

        {phase.kind === "idle" && (
          <Button onClick={start}>
            <Mic data-icon="inline-start" />
            Start recording
          </Button>
        )}

        {phase.kind === "recording" && (
          <div className="flex items-center gap-2">
            <span className="size-2 animate-pulse rounded-full bg-destructive" />
            <span className="font-mono tabular-nums">{clock(elapsed)}</span>
            <span className="text-xs text-muted-foreground">/ {clock(MAX_SECONDS)}</span>
            <div className="ml-auto flex gap-1">
              <Button variant="ghost" size="icon-sm" aria-label="Discard" onClick={() => stop(true)}>
                <Trash2 />
              </Button>
              <Button size="sm" onClick={() => stop(false)}>
                <Square data-icon="inline-start" />
                Stop
              </Button>
            </div>
          </div>
        )}

        {(phase.kind === "processing" || phase.kind === "sending") && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Spinner />
            {phase.kind === "processing" ? "Preparing…" : "Sending…"}
          </div>
        )}

        {phase.kind === "review" && (
          <div className="flex flex-col gap-2">
            <audio controls src={phase.url} className="h-9 w-full" />
            <div className="flex justify-end gap-1">
              <Button variant="ghost" size="sm" onClick={() => setPhase({ kind: "idle" })}>
                <Trash2 data-icon="inline-start" />
                Discard
              </Button>
              <Button size="sm" onClick={submit}>
                <Send data-icon="inline-start" />
                Send to Veez
              </Button>
            </div>
          </div>
        )}

        {error && <p className="text-xs text-destructive">{error}</p>}

        <RecentNotes />
      </PopoverContent>
    </Popover>
  );
}

function RecentNotes() {
  const notes = useQuery(api.voiceNotes.recent);
  const retry = useMutation(api.voiceNotes.retry);
  if (!notes?.length) return null;
  return (
    <div className="flex flex-col gap-1.5 border-t pt-2.5">
      <span className="text-xs font-medium text-muted-foreground">Recent</span>
      {notes.map((n) => (
        <div key={n._id} className="flex items-center gap-2 text-xs">
          <span className="font-mono tabular-nums">{clock(n.durationSecs)}</span>
          <span className="min-w-0 flex-1 truncate text-muted-foreground" title={n.error}>
            {n.status === "sending" && "Sending…"}
            {n.status === "sent" && `Sent ${formatDistanceToNow(n._creationTime, { addSuffix: true })}`}
            {n.status === "failed" && <span className="text-destructive">Failed — {n.error}</span>}
          </span>
          {n.status === "sent" && n.threadUrl && (
            <a
              href={n.threadUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              Thread
              <ExternalLink className="size-3" />
            </a>
          )}
          {n.status === "failed" && (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label="Retry"
              onClick={() => void retry({ id: n._id }).catch(() => {})}
            >
              <RotateCcw />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
